
import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign, ArrowLeft, ArrowRight, TrendingUp, Calculator, PieChart, Sparkles, Hotel, Plane, Car, MapPin, Package, Eye, EyeOff } from "lucide-react";
import { PackageData } from "@/pages/CreatePackage";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { ProgressSteps } from "./ProgressSteps";

interface PricingStepProps {
  data: PackageData;
  onUpdate: (data: Partial<PackageData>) => void;
  onNext: () => void;
  onPrev: () => void;
  completedSteps?: number[];
  onStepClick?: (step: number) => void;
  isStepRequired?: (step: number) => boolean;
}

export const PricingStep = ({ 
  data, 
  onUpdate, 
  onNext, 
  onPrev, 
  completedSteps = [], 
  onStepClick = () => {}, 
  isStepRequired = () => true 
}: PricingStepProps) => {
  const [discount, setDiscount] = useState(data.discount || 0);
  const [taxes, setTaxes] = useState(data.taxes || 0);
  const [paymentTerms, setPaymentTerms] = useState(data.paymentTerms || '');
  const [profitType, setProfitType] = useState<'percentage' | 'fixed'>(data.profitType || 'percentage');
  const [profitValue, setProfitValue] = useState(data.profitValue || 20);

  const priceBreakdown = useMemo(() => {
    const hotelsTotal = data.includesHotels ? data.hotels.reduce((acc, hotel) => acc + (hotel.pricePerNight * hotel.nights), 0) : 0;
    const flightsTotal = (data.includesInternationalFlights || data.includesDomesticFlights) ? data.flights.reduce((acc, flight) => acc + (flight.sellingPrice || flight.price || 0), 0) : 0;
    const transportTotal = data.includesTransport ? data.transport.reduce((acc, item) => acc + item.price, 0) : 0;
    const toursTotal = data.includesTours ? data.tours.reduce((acc, tour) => acc + tour.price, 0) : 0;
    const servicesTotal = data.includesServices ? data.services.reduce((acc, service) => acc + (service.price || 0), 0) : 0;
    
    return { hotelsTotal, flightsTotal, transportTotal, toursTotal, servicesTotal };
  }, [data.hotels, data.flights, data.transport, data.tours, data.services, data.includesHotels, data.includesInternationalFlights, data.includesDomesticFlights, data.includesTransport, data.includesTours, data.includesServices]);

  const basePrice = useMemo(() => {
    return Object.values(priceBreakdown).reduce((sum, current) => sum + current, 0);
  }, [priceBreakdown]);

  const profitAmount = useMemo(() => {
    if (profitType === 'percentage') {
      return basePrice * (profitValue / 100);
    }
    return profitValue;
  }, [basePrice, profitType, profitValue]);

  const totalPrice = useMemo(() => {
    const calculatedTotal = basePrice + profitAmount - discount + taxes;
    return Math.max(0, calculatedTotal);
  }, [basePrice, profitAmount, discount, taxes]);

  useEffect(() => {
    onUpdate({
      basePrice,
      profitType,
      profitValue,
      profitAmount,
      discount,
      taxes,
      totalPrice,
      paymentTerms
    });
  }, [basePrice, profitType, profitValue, profitAmount, discount, taxes, totalPrice, paymentTerms, onUpdate]);

  const getCostBreakdownItems = () => {
    const items = [];
    if (data.includesHotels && priceBreakdown.hotelsTotal > 0) {
      items.push({ name: 'إجمالي الفنادق', value: priceBreakdown.hotelsTotal, icon: Hotel, color: 'text-blue-600' });
    }
    if ((data.includesInternationalFlights || data.includesDomesticFlights) && priceBreakdown.flightsTotal > 0) {
      items.push({ name: 'إجمالي الطيران', value: priceBreakdown.flightsTotal, icon: Plane, color: 'text-sky-600' });
    }
    if (data.includesTransport && priceBreakdown.transportTotal > 0) {
      items.push({ name: 'إجمالي المواصلات', value: priceBreakdown.transportTotal, icon: Car, color: 'text-green-600' });
    }
    if (data.includesTours && priceBreakdown.toursTotal > 0) {
      items.push({ name: 'إجمالي الجولات السياحية', value: priceBreakdown.toursTotal, icon: MapPin, color: 'text-purple-600' });
    }
    if (data.includesServices && priceBreakdown.servicesTotal > 0) {
      items.push({ name: 'إجمالي الخدمات الإضافية', value: priceBreakdown.servicesTotal, icon: Package, color: 'text-orange-600' });
    }
    return items;
  };

  return (
    <Card className="w-full bg-gradient-to-br from-emerald-50/50 to-green-50/50 border-2 border-emerald-100 shadow-lg">
      <CardHeader className="bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-t-lg">
        <CardTitle className="flex items-center gap-3 text-2xl">
          <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
            <DollarSign className="w-7 h-7" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              التسعير والعروض المالية
              <TrendingUp className="w-5 h-5 opacity-75" />
            </div>
          </div>
        </CardTitle>
        <CardDescription className="text-emerald-100 text-lg">
          حدد تفاصيل الأسعار والخصومات والشروط المالية بناءً على الخدمات المختارة
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-8 p-8">
        
        {/* Price Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border-2 border-blue-200 text-center shadow-lg hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-center mb-3">
              <Calculator className="w-6 h-6 text-blue-600" />
            </div>
            <p className="text-sm text-blue-600 font-medium mb-2">السعر الأساسي (التكلفة)</p>
            <p className="text-2xl font-bold text-blue-800">{basePrice.toLocaleString()} SAR</p>
          </div>
          
          <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border-2 border-green-200 text-center shadow-lg hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-center mb-3">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-sm text-green-600 font-medium mb-2">الربح المضاف</p>
            <p className="text-2xl font-bold text-green-800">+ {profitAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })} SAR</p>
          </div>
          
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border-2 border-purple-200 text-center shadow-lg hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-center mb-3">
              <PieChart className="w-6 h-6 text-purple-600" />
            </div>
            <p className="text-sm text-purple-600 font-medium mb-2">الخصم / الضرائب</p>
            <p className="text-xl font-bold">
              <span className="text-red-600">- {discount.toLocaleString()}</span>
              <span className="text-purple-800 mx-1">/</span>
              <span className="text-green-600">+ {taxes.toLocaleString()}</span>
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-amber-50 to-yellow-100 p-6 rounded-xl border-2 border-amber-200 text-center shadow-lg hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-center mb-3">
              <DollarSign className="w-6 h-6 text-amber-600" />
            </div>
            <p className="text-sm font-semibold text-amber-600 mb-2">السعر الإجمالي النهائي</p>
            <p className="text-3xl font-extrabold text-amber-800">{totalPrice.toLocaleString()} SAR</p>
          </div>
        </div>

        {/* Cost Breakdown Section */}
        {getCostBreakdownItems().length > 0 && (
          <div className="bg-gradient-to-br from-gray-50 to-slate-50 p-6 rounded-xl shadow-sm border-2 border-gray-200">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-gray-500 to-slate-500 rounded-xl shadow-lg">
                <PieChart className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-xl font-bold text-gray-800">تفاصيل التكلفة الأساسية</h4>
              <div className="flex-1 h-px bg-gradient-to-r from-gray-300 to-transparent"></div>
              <Badge variant="secondary" className="bg-gray-100 text-gray-700">
                {getCostBreakdownItems().length} خدمة
              </Badge>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {getCostBreakdownItems().map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div key={index} className="bg-white p-4 rounded-lg border-2 border-gray-100 hover:border-gray-300 transition-all duration-300 shadow-sm hover:shadow-md">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <IconComponent className={`w-5 h-5 ${item.color}`} />
                        <span className="text-gray-700 font-medium">{item.name}</span>
                      </div>
                      <strong className="text-gray-800 font-bold">{item.value.toLocaleString()} SAR</strong>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Profit and Adjustments Section */}
        <div className="bg-gradient-to-br from-indigo-50 to-blue-50 p-6 rounded-xl shadow-sm border-2 border-indigo-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-gradient-to-br from-indigo-500 to-blue-500 rounded-xl shadow-lg">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <h4 className="text-xl font-bold text-gray-800">تعديلات السعر والربح</h4>
            <div className="flex-1 h-px bg-gradient-to-r from-indigo-200 via-blue-200 to-transparent"></div>
            <Sparkles className="w-5 h-5 text-indigo-400" />
          </div>
          
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg border-2 border-indigo-100">
              <Label className="font-bold text-gray-800 mb-4 block text-lg">💰 تحديد الربح</Label>
              <RadioGroup
                value={profitType}
                onValueChange={(value) => setProfitType(value as 'percentage' | 'fixed')}
                className="flex gap-6 mb-4"
              >
                <div className="flex items-center space-x-2 space-x-reverse bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <RadioGroupItem value="percentage" id="r_percentage" className="border-blue-400 text-blue-600" />
                  <Label htmlFor="r_percentage" className="cursor-pointer font-medium text-blue-800">📊 نسبة مئوية (%)</Label>
                </div>
                <div className="flex items-center space-x-2 space-x-reverse bg-green-50 p-3 rounded-lg border border-green-200">
                  <RadioGroupItem value="fixed" id="r_fixed" className="border-green-400 text-green-600" />
                  <Label htmlFor="r_fixed" className="cursor-pointer font-medium text-green-800">💵 مبلغ ثابت (SAR)</Label>
                </div>
              </RadioGroup>
              <div className="relative">
                <Input
                  id="profitValue"
                  type="number"
                  value={profitValue}
                  onChange={(e) => setProfitValue(Number(e.target.value) || 0)}
                  className="text-xl pl-16 h-14 border-2 focus:border-indigo-500 bg-white shadow-sm font-bold"
                  min="0"
                  step={profitType === 'percentage' ? '0.1' : '1'}
                />
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-indigo-600 text-xl font-bold">
                  {profitType === 'percentage' ? '%' : 'SAR'}
                </span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg border-2 border-red-100">
                <Label htmlFor="discount" className="font-bold text-red-800 mb-3 block flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                  مبلغ الخصم (SAR)
                </Label>
                <Input
                  id="discount"
                  type="number"
                  value={discount}
                  onChange={(e) => setDiscount(Number(e.target.value) || 0)}
                  className="text-xl h-14 border-2 focus:border-red-500 bg-white shadow-sm font-bold"
                  min="0"
                  placeholder="0"
                />
              </div>
              
              <div className="bg-white p-6 rounded-lg border-2 border-amber-100">
                <Label htmlFor="taxes" className="font-bold text-amber-800 mb-3 block flex items-center gap-2">
                  <div className="w-4 h-4 bg-amber-500 rounded-full"></div>
                  مبلغ الضرائب أو الرسوم (SAR)
                </Label>
                <Input
                  id="taxes"
                  type="number"
                  value={taxes}
                  onChange={(e) => setTaxes(Number(e.target.value) || 0)}
                  className="text-xl h-14 border-2 focus:border-amber-500 bg-white shadow-sm font-bold"
                  min="0"
                  placeholder="0"
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Payment Terms Section */}
        <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-6 rounded-xl shadow-sm border-2 border-teal-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl shadow-lg">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <h4 className="text-xl font-bold text-gray-800">شروط الدفع والسداد</h4>
            <div className="flex-1 h-px bg-gradient-to-r from-teal-200 via-cyan-200 to-transparent"></div>
          </div>
          
          <div className="space-y-3">
            <Label htmlFor="paymentTerms" className="font-bold text-teal-800 text-lg">📋 شروط الدفع</Label>
            <Textarea
              id="paymentTerms"
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
              placeholder="💳 مثال: 50% عند الحجز، 50% قبل السفر بأسبوع..."
              rows={5}
              className="border-2 focus:border-teal-500 bg-white shadow-sm resize-none text-lg"
            />
            <p className="text-sm text-teal-600 bg-teal-50 p-3 rounded-lg border border-teal-200">
              💡 حدد كيفية تقسيم المدفوعات وتواريخ الاستحقاق لتسهيل عملية التحصيل
            </p>
          </div>
        </div>

        <div className="flex justify-between pt-8 border-t-2 border-gray-200">
          <Button 
            variant="outline" 
            onClick={onPrev}
            className="h-12 px-6 border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-50"
          >
            <ArrowRight className="w-5 h-5 ml-2" />
            السابق
          </Button>
          <Button 
            onClick={onNext} 
            className="h-12 px-8 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
          >
            التالي
            <ArrowLeft className="w-5 h-5 mr-2" />
          </Button>
        </div>
      </CardContent>
      
      {/* Progress Steps */}
      <ProgressSteps 
        currentStep={8} 
        completedSteps={completedSteps} 
        onStepClick={onStepClick} 
        isStepRequired={isStepRequired} 
      />
    </Card>
  );
};
